import { useState } from 'react';
import { createPortal } from 'react-dom';
import { doc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faStar, 
  faMapMarkerAlt, 
  faPhone, 
  faEnvelope, 
  faGlobe, 
  faTimes,
  faCheck,
  faCalendar
} from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

export default function VendorDetailModal({ 
  vendor, 
  experienceId, 
  selectedTask, 
  onSelect, 
  onClose 
}) {
  const { user } = useAuth();
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [inquiryMessage, setInquiryMessage] = useState('');

  const handleSendInquiry = async () => {
    if (!inquiryMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      await addDoc(collection(db, 'inquiries'), {
        userId: user?.uid,
        vendorId: vendor.id,
        experienceId: experienceId,
        taskId: selectedTask?.id,
        status: 'pending',
        messages: [
          {
            from: 'user',
            text: inquiryMessage,
            timestamp: serverTimestamp()
          }
        ],
        createdAt: serverTimestamp()
      });

      toast.success('Inquiry sent! The vendor will contact you soon.');
      setShowInquiryForm(false);
      setInquiryMessage('');
    } catch (error) {
      console.error('Error sending inquiry:', error);
      toast.error('Failed to send inquiry');
    }
  };

  const handleSelectVendor = async () => {
    if (selectedTask && experienceId) {
      try {
        // Update task with linked vendor
        const { getDoc, updateDoc } = await import('firebase/firestore');
        const experienceRef = doc(db, 'projects', experienceId);
        const experienceDoc = await getDoc(experienceRef);
        
        const experienceData = experienceDoc.data();
        const updatedTasks = (experienceData.tasks || []).map(task =>
          task.id === selectedTask.id
            ? { ...task, linkedVendor: vendor.id, linkedVendorName: vendor.name }
            : task
        );

        await updateDoc(experienceRef, {
          tasks: updatedTasks
        });

        toast.success(`Linked ${vendor.name} to "${selectedTask.title}"`);
        if (onSelect) onSelect(vendor);
      } catch (error) {
        console.error('Error linking vendor:', error);
        toast.error('Failed to link vendor');
      }
    } else {
      if (onSelect) onSelect(vendor);
    }
  };

  const rating = vendor.averageRating || 0;
  const reviewCount = vendor.reviewCount || 0;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 z-[120] flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-text-primary mb-2">{vendor.name}</h2>
            <div className="flex items-center gap-4 flex-wrap">
              {/* Rating */}
              <div className="flex items-center gap-1">
                <FontAwesomeIcon icon={faStar} className="text-yellow-400" />
                <span className="font-semibold text-text-primary">{rating.toFixed(1)}</span>
                {reviewCount > 0 && (
                  <span className="text-sm text-text-secondary ml-1">
                    ({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})
                  </span>
                )}
              </div>

              {/* Location */}
              {vendor.location && (
                <div className="flex items-center gap-2 text-sm text-text-secondary">
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  <span>{vendor.location}</span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-text-secondary" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Image Gallery */}
          {vendor.images && vendor.images.length > 0 && (
            <div className="mb-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {vendor.images.slice(0, 6).map((image, index) => (
                  <div key={index} className="aspect-square rounded-lg overflow-hidden">
                    <img 
                      src={image} 
                      alt={`${vendor.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Description */}
          {vendor.description && (
            <div className="mb-6">
              <h3 className="text-lg font-bold text-text-primary mb-2">About</h3>
              <p className="text-text-secondary leading-relaxed">{vendor.description}</p>
            </div>
          )}

          {/* Categories */}
          {vendor.categories && vendor.categories.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-bold text-text-primary mb-2">Services</h3>
              <div className="flex flex-wrap gap-2">
                {vendor.categories.map(category => (
                  <span
                    key={category}
                    className="px-3 py-1 bg-primary/10 text-primary rounded-full font-semibold"
                  >
                    {category}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Price Range */}
          {vendor.priceRange && (
            <div className="mb-6">
              <h3 className="text-lg font-bold text-text-primary mb-2">Pricing</h3>
              <p className="text-text-secondary">
                {vendor.priceRange.min === vendor.priceRange.max ? (
                  `$${vendor.priceRange.min.toLocaleString()}`
                ) : (
                  `$${vendor.priceRange.min.toLocaleString()} - $${vendor.priceRange.max.toLocaleString()}`
                )}
              </p>
            </div>
          )}

          {/* Contact Information */}
          <div className="mb-6">
            <h3 className="text-lg font-bold text-text-primary mb-3">Contact</h3>
            <div className="space-y-2">
              {vendor.phone && (
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faPhone} className="text-primary" />
                  <a 
                    href={`tel:${vendor.phone}`}
                    className="text-primary hover:underline"
                  >
                    {vendor.phone}
                  </a>
                </div>
              )}
              {vendor.email && (
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faEnvelope} className="text-primary" />
                  <a 
                    href={`mailto:${vendor.email}`}
                    className="text-primary hover:underline"
                  >
                    {vendor.email}
                  </a>
                </div>
              )}
              {vendor.website && (
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faGlobe} className="text-primary" />
                  <a 
                    href={vendor.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Visit Website
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Reviews Preview */}
          {vendor.reviews && vendor.reviews.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-bold text-text-primary mb-3">Recent Reviews</h3>
              <div className="space-y-4">
                {vendor.reviews.slice(0, 3).map((review, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <FontAwesomeIcon
                            key={i}
                            icon={faStar}
                            className={i < review.rating ? 'text-yellow-400' : 'text-gray-300'}
                          />
                        ))}
                      </div>
                      <span className="text-sm font-semibold text-text-primary">
                        {review.userName || 'Anonymous'}
                      </span>
                      <span className="text-xs text-text-secondary">
                        {review.date?.toDate ? review.date.toDate().toLocaleDateString() : ''}
                      </span>
                    </div>
                    <p className="text-text-secondary text-sm">{review.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Inquiry Form */}
          {showInquiryForm && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-border">
              <h3 className="text-lg font-bold text-text-primary mb-3">Send Inquiry</h3>
              <textarea
                value={inquiryMessage}
                onChange={(e) => setInquiryMessage(e.target.value)}
                placeholder="Tell the vendor about your needs..."
                className="w-full min-h-[120px] p-4 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary resize-none mb-3"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSendInquiry}
                  className="px-6 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors"
                >
                  Send Inquiry
                </button>
                <button
                  onClick={() => {
                    setShowInquiryForm(false);
                    setInquiryMessage('');
                  }}
                  className="px-6 py-2 bg-white border border-border rounded-lg font-semibold hover:border-primary transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-border flex gap-3">
          {selectedTask && (
            <button
              onClick={handleSelectVendor}
              className="flex-1 px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors flex items-center justify-center gap-2"
            >
              <FontAwesomeIcon icon={faCheck} />
              Link to "{selectedTask.title}"
            </button>
          )}
          <button
            onClick={() => setShowInquiryForm(!showInquiryForm)}
            className="px-6 py-3 bg-white border-2 border-border rounded-lg font-semibold hover:border-primary transition-colors flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faCalendar} />
            Contact Vendor
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
